import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-modal";
import { canManageCourierZones } from "../../utils/helper";
import CourierListPage, { CourierListHeadAction } from "./courier-list-page";
import CreateZoneForm from "./forms/create-zone-form";
import EditZoneForm from "./forms/edit-zone-form";
import Confirmation from "../commons/confirmation";
import CourierTable from "./ui/courier-table";
import CourierBooleanTag from "./ui/courier-boolean-tag";
import RowActions from "./ui/row-actions";
import { useCourierMutationDone } from "./ui/use-courier-mutation-done";

const ZonesList = props => {
  const {
    getCourierZones,
    zones = [],
    courierLoading,
    createCourierZone,
    updateCourierZone,
    deleteCourierZone,
    reorderCourierZones,
    courierApiError,
    resetCourierState,
    getCities,
    cities = []
  } = props;

  const [isOpenCreateModal, setIsOpenCreateModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const canManage = canManageCourierZones();

  useCourierMutationDone(courierLoading, courierApiError, () => {
    setIsOpenCreateModal(false);
    setEditTarget(null);
  });

  const orderedZones = useMemo(
    () =>
      [...zones].sort(
        (a, b) =>
          (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name)
      ),
    [zones]
  );

  useEffect(() => {
    getCourierZones({});
    getCities && getCities({ vertical: "" });
    return () => {
      resetCourierState();
    };
  }, []);

  const moveZone = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= orderedZones.length) return;
    const ids = orderedZones.map(z => z.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    reorderCourierZones({ zoneIds: ids });
  };

  return (
    <CourierListPage
      title="Courier Zones"
      count={zones.length}
      loading={courierLoading}
      headActions={
        canManage && (
          <CourierListHeadAction
            label="Add Zone"
            onClick={() => setIsOpenCreateModal(true)}
          />
        )
      }
    >
      <CourierTable>
        <thead>
          <tr>
            <th className="seq">Seq</th>
            <th>Name</th>
            <th>Code</th>
            <th>City</th>
            <th>Centers</th>
            <th>Special</th>
            <th className="status">Active</th>
            {canManage && <th className="act">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {orderedZones.map((z, index) => (
            <tr key={z.id}>
              <td className="seq">
                {z.sortOrder ?? index + 1}
                {canManage && (
                  <>
                    <button
                      type="button"
                      className="courier-move"
                      disabled={index === 0}
                      onClick={() => moveZone(index, -1)}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="courier-move"
                      disabled={index === orderedZones.length - 1}
                      onClick={() => moveZone(index, 1)}
                    >
                      ↓
                    </button>
                  </>
                )}
              </td>
              <td>
                {z.name}
                <span className="sm-text">ID {z.id}</span>
              </td>
              <td>{z.code || "—"}</td>
              <td>{z.cityName}</td>
              <td>{z.centersCount}</td>
              <td className="status">
                <CourierBooleanTag value={z.isSpecial} />
              </td>
              <td className="status">
                <CourierBooleanTag value={z.isActive} />
              </td>
              {canManage && (
                <td className="act">
                  <RowActions
                    onEdit={() => setEditTarget(z)}
                    onDelete={() => setDeleteTarget(z)}
                  />
                </td>
              )}
            </tr>
          ))}
          {!zones.length && !courierLoading && (
            <tr>
              <td colSpan={canManage ? 8 : 7}>No zones found.</td>
            </tr>
          )}
        </tbody>
      </CourierTable>

      {isOpenCreateModal && (
        <Modal
          isOpen={isOpenCreateModal}
          onRequestClose={() => setIsOpenCreateModal(false)}
          className="small-modal"
        >
          <CreateZoneForm
            closeModal={() => setIsOpenCreateModal(false)}
            loading={courierLoading}
            cities={cities}
            apiError={courierApiError}
            createCourierZone={createCourierZone}
          />
        </Modal>
      )}

      {editTarget && (
        <Modal
          isOpen={!!editTarget}
          onRequestClose={() => setEditTarget(null)}
          className="small-modal"
        >
          <EditZoneForm
            zone={editTarget}
            cities={cities}
            closeModal={() => setEditTarget(null)}
            loading={courierLoading}
            apiError={courierApiError}
            updateCourierZone={updateCourierZone}
          />
        </Modal>
      )}

      {deleteTarget && (
        <Modal
          isOpen={!!deleteTarget}
          onRequestClose={() => setDeleteTarget(null)}
          className="small-modal"
        >
          <Confirmation
            text={`Delete zone "${deleteTarget.name}"?`}
            onConfirm={() => {
              deleteCourierZone(deleteTarget.id);
              setDeleteTarget(null);
            }}
            onClose={() => setDeleteTarget(null)}
          />
        </Modal>
      )}
    </CourierListPage>
  );
};

export default ZonesList;
